import React, { useState } from "react";
import {
  Drawer,
  List,
  Toolbar,
  Divider,
  Collapse,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Box,
  useMediaQuery,
  useTheme,
  Avatar,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import NavItem from "./SidebarNavItem";

import {
  Book as BookIcon,
  ViewKanban as KanbanIcon,
  ExpandLess,
  ExpandMore,
  Settings as SettingsIcon,
  Home as HomeIcon,
  Help as HelpIcon,
  RecentActors,
} from "@mui/icons-material";
import { Wallet } from "lucide-react";
import { useCurrentTalentProfile } from "../hooks";
import useCurrentBusinessInfo from "../hooks/useCurrentBusiness";

const drawerWidth = 240;

/* ── Netflix-ish tokens (design only) ───────────────────────────── */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "rgba(255,255,255,0.92)",
  textMuted: "rgba(255,255,255,0.70)",
  red: "#E50914",
  redHover: "#b20710",
};

type BusinessSidebarProps = {
  open: boolean;
  handleClose?: () => void;
};

const BusinessSidebar = ({ open, handleClose }: BusinessSidebarProps) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [bookingOpen, setBookingOpen] = useState(false);
  const { currentBusiness } = useCurrentBusinessInfo();

  console.log("currentBusiness", "here here");

  const handleBookingToggle = () => setBookingOpen(!bookingOpen);

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      onClose={handleClose}
      sx={{
        width: open ? drawerWidth : 72,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? drawerWidth : 72,
          transition: "width 240ms ease",
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.98), rgba(10,10,10,0.92))",
          borderRight: `1px solid ${COLORS.border}`,
          overflowX: "hidden",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          color: COLORS.text,
          boxShadow:
            "inset 0 0 0 1px rgba(255,255,255,0.03), 2px 0 24px rgba(0,0,0,0.4)",
          backdropFilter: "blur(6px)",
          px: open ? 1 : 0,

          /* Global item styles (applies to NavItem internals too) */
          "& .MuiListItemButton-root": {
            color: COLORS.textMuted,
            borderRadius: 8,
            mx: open ? 0.5 : 0.75,
            px: open ? 1.25 : 1,
            py: 1,
            justifyContent: open ? "flex-start" : "center",
            position: "relative",
            overflow: "hidden",
            transition: "background-color 160ms, color 160ms, transform 160ms",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.06)",
              color: COLORS.text,
            },
            "&:hover::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 8,
              bottom: 8,
              width: 2,
              borderRadius: 2,
              backgroundColor: COLORS.red,
              opacity: 0.65,
            },
          },
          "& .MuiListItemButton-root.Mui-selected": {
            background: "rgba(229,9,20,0.16)",
            color: COLORS.text,
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 8,
              bottom: 8,
              width: 3,
              borderRadius: 2,
              backgroundColor: COLORS.red,
            },
            "&:hover": {
              background: "rgba(229,9,20,0.22)",
            },
          },
          "& .MuiListItemIcon-root": {
            minWidth: 0,
            mr: open ? 1.25 : 0,
            color: COLORS.text,
            justifyContent: "center",
          },
          "& .MuiListItemText-root": {
            opacity: open ? 1 : 0,
            maxWidth: open ? "100%" : 0,
            transition: "opacity 160ms",
          },
          "& .MuiDivider-root": { borderColor: COLORS.border },
        },
      }}
    >
      <Box>
        {/* Keep the spacer Toolbar */}
        <Toolbar />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            px: open ? 1 : 0,
          }}
        >
          {/* Profile */}
          <List
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: 0.75, pt: 3 }}
          >
            <NavItem
              item={{
                text: currentBusiness?.business_name ?? "Profile",
                icon: (
                  <Avatar
                    src={currentBusiness?.profile_image ?? ""}
                    sx={{
                      width: 28,
                      height: 28,
                      border: `1px solid ${COLORS.border}`,
                    }}
                  >
                    {currentBusiness?.business_name?.[0]}
                  </Avatar>
                ),
                path: "/talent/profile",
              }}
              open={open}
              onClick={isMobile ? handleClose : undefined}
              selected={location.pathname === "/talent/profile"}
            />
          </List>

          <Divider sx={{ width: "80%", mx: "auto" }} />

          <Divider sx={{ width: "80%", mx: "auto" }} />

          <NavItem
            item={{
              text: "Stage cards",
              icon: <RecentActors />,
              path: "/stage-cards",
            }}
            open={open}
            selected={location.pathname === "/schedules"}
          />

          <Divider sx={{ width: "80%", mx: "auto" }} />

          {/* Main Section */}
          <List
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
          >
            <NavItem
              item={{ text: "TN", icon: <HomeIcon />, path: "/TNpage" }}
              open={open}
              selected={location.pathname === "/TNpage"}
              onClick={isMobile ? handleClose : undefined}
            />
            <NavItem
              item={{
                text: "Dashboard",
                icon: <KanbanIcon />,
                path: "/talent-dashboard",
              }}
              open={open}
              selected={location.pathname === "/talent-dashboard"}
              onClick={isMobile ? handleClose : undefined}
            />
            {/* <NavItem
              item={{ text: "Manage Schedule", icon: <TimeIcon />, path: "/schedules" }}
              open={open}
              selected={location.pathname === "/schedules"}
              onClick={isMobile ? handleClose : undefined}
            /> */}

            {/* Bookings */}
            <ListItemButton
              onClick={handleBookingToggle}
              sx={{
                borderRadius: 2,
                minHeight: 50,
                mx: 1,
                justifyContent: open ? "flex-start" : "center",
                px: open ? 1.25 : 1,
                py: 1,
                color: COLORS.textMuted,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.06)",
                  color: COLORS.text,
                  "& .MuiListItemIcon-root": { color: COLORS.text },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 1.25 : 0,
                  justifyContent: "center",
                  color: COLORS.text,
                  transition: "color 0.2s ease",
                  fontSize: "1.1rem",
                }}
              >
                <BookIcon />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Bookings"
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      color: COLORS.text,
                    },
                  }}
                />
              )}
              {open &&
                (bookingOpen ? (
                  <ExpandLess sx={{ color: COLORS.text }} />
                ) : (
                  <ExpandMore sx={{ color: COLORS.text }} />
                ))}
            </ListItemButton>

            <Collapse in={bookingOpen} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ pl: open ? 4 : 1, gap: 0.5 }}>
                {/* <NavItem
                  item={{ text: " Suits Bookings", icon: <BookIcon />, path: "/bookings" }}
                  open={open}
                  selected={location.pathname === "/bookings"}
                  onClick={isMobile ? handleClose : undefined}
                /> */}
                <NavItem
                  item={{
                    text: "StageCard Bookings",
                    icon: <BookIcon />,
                    path: "/bookings/stagecard",
                  }}
                  open={open}
                  selected={location.pathname === "/bookings/stagecard"}
                  onClick={isMobile ? handleClose : undefined}
                />
              </List>
            </Collapse>

            <NavItem
              item={{ text: "Wallet", icon: <Wallet />, path: "/wallet" }}
              open={open}
              selected={location.pathname === "/wallet"}
              onClick={isMobile ? handleClose : undefined}
            />
            {/* <NavItem ... URC (kept as comment exactly as-is) /> */}
            {/* <NavItem
              item={{ text: "GTP", icon: <KanbanIcon />, path: "/gtp" }}
              open={open}
              selected={location.pathname === "/gtp"}
              onClick={isMobile ? handleClose : undefined}
            /> */}
          </List>
        </Box>
      </Box>

      {/* Bottom fixed section */}
      <Box sx={{ mb: 2, px: open ? 0.5 : 0 }}>
        <List
          disablePadding
          sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
        >
          {[
            { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
            { text: "Help", icon: <HelpIcon />, path: "/help" },
          ].map((item) => (
            <NavItem
              key={item.text}
              item={item}
              open={open}
              selected={location.pathname === item.path}
              onClick={isMobile ? handleClose : undefined}
            />
          ))}
        </List>
        {/* Subtle gradient rule */}
        <Box
          sx={{
            mt: 1,
            mx: open ? 1.25 : 0.75,
            height: 1,
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
            borderRadius: 1,
          }}
        />
      </Box>
    </Drawer>
  );
};

export default BusinessSidebar;
